import type { OrganizationDto } from "@intelligems/ig-types/dtos/organizations";
import type {
  PostPurchaseReadiness,
  PostPurchaseReadinessIssue,
  PostPurchaseSetupIssue,
} from "@intelligems/ig-types/dtos/post-purchase";

import {
  hasEnabledConfig,
  POST_PURCHASE_LAUNCH_TOGGLE,
  POST_PURCHASE_PLAN_SCOPE,
} from "./resolve-available-test-types";

type PostPurchaseSetup = {
  hasWriteOrders: boolean;
  hasStorefrontToken: boolean;
  hasCartMetafields: boolean;
};

/**
 * Everything a store still needs before a post-purchase upsell can work, in
 * the order the merchant has to fix it. Refreshing Shopify permissions fixes
 * the first two; support turns on cart metafields.
 */
const getPostPurchaseSetupIssues = (
  options: PostPurchaseSetup
): PostPurchaseSetupIssue[] => [
  ...(options.hasWriteOrders ? [] : (["writeOrders"] as const)),
  ...(options.hasStorefrontToken ? [] : (["storefrontToken"] as const)),
  ...(options.hasCartMetafields ? [] : (["cartMetafields"] as const)),
];

/** The first thing a store still needs, or null once it's ready. */
export const getPostPurchaseSetupIssue = (
  options: PostPurchaseSetup
): PostPurchaseSetupIssue | null =>
  getPostPurchaseSetupIssues(options)[0] ?? null;

export const PostPurchaseSetupStartMessages: Record<
  PostPurchaseSetupIssue,
  string
> = {
  writeOrders:
    "Refresh your Shopify permissions to start this post-purchase upsell",
  storefrontToken:
    "Refresh your Shopify permissions to start this post-purchase upsell",
  cartMetafields:
    "Contact support to enable cart metafields before starting this post-purchase upsell",
};

// Same wording as the dashboard's Start button (experiment-scope-hooks.tsx)
const PostPurchaseReadinessMessages: Record<
  PostPurchaseReadinessIssue,
  string
> = {
  notOnPlan: "Upgrade your plan to start this post-purchase upsell",
  notLaunched: "Launching post purchase coming soon",
  ...PostPurchaseSetupStartMessages,
};

const CART_METAFIELDS_CONFIG = {
  topic: "PLUGIN_CONFIG",
  key: "enable_cart_metafields",
} as const;

const getPostPurchaseReadinessIssues = (
  org: Pick<
    OrganizationDto,
    "scopes" | "hasStorefrontToken" | "configurations"
  >,
  options: { skipPlanCheck?: boolean }
): PostPurchaseReadinessIssue[] => [
  ...(options.skipPlanCheck ||
  hasEnabledConfig(org.configurations, POST_PURCHASE_PLAN_SCOPE)
    ? []
    : (["notOnPlan"] as const)),
  ...(hasEnabledConfig(org.configurations, POST_PURCHASE_LAUNCH_TOGGLE)
    ? []
    : (["notLaunched"] as const)),
  ...getPostPurchaseSetupIssues({
    hasWriteOrders: org.scopes?.includes("write_orders") ?? false,
    hasStorefrontToken: org.hasStorefrontToken,
    hasCartMetafields: hasEnabledConfig(
      org.configurations,
      CART_METAFIELDS_CONFIG
    ),
  }),
];

/**
 * Whether a store can run a post-purchase upsell, and if not, everything to
 * fix, in the same order and wording as the dashboard's Start button, so an
 * agent can name it all at once. Missing write_orders and a missing
 * storefront token have the same fix, so it's said once. Takes the org record
 * both agents already fetch. Billing isn't checked: that needs the billing
 * snapshot, which the org record doesn't carry. A trusted staff Start call
 * may skip the plan check; the launch and setup checks still run.
 */
export const getPostPurchaseReadiness = (
  org: Pick<
    OrganizationDto,
    "scopes" | "hasStorefrontToken" | "configurations"
  >,
  options: { skipPlanCheck?: boolean } = {}
): PostPurchaseReadiness => {
  const issues = getPostPurchaseReadinessIssues(org, options);
  if (issues.length === 0) {
    return { ready: true };
  }
  const messages = new Set(
    issues.map((issue) => PostPurchaseReadinessMessages[issue])
  );
  return { ready: false, issues, message: [...messages].join(". ") };
};
